import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { JobResponseEntity } from "@/src/hooks/api/generated";

import {
  evaluateAutomaticApply,
  IAutomaticApplyHandoffClient,
} from "../utils/automaticApplyEligibility";
import {
  AutomaticApplySkipReason,
  describeError,
  IAutomaticApplySkip,
} from "../utils/automaticApplySkipReason";
import { buildJobApplyUrl } from "../utils/buildJobApplyUrl";

const CONFIG_ENDPOINT = "/api/jobs/automatic-apply";
const CONFIG_FETCH_TIMEOUT_MS = 2000;

/**
 * "pending" is distinct from "ineligible" so the caller can hold the
 * interstitial up while the decision is still in flight, rather than showing
 * the job page and snatching it away a moment later.
 */
export type AutomaticApplyHandoffStatus = "pending" | "eligible" | "ineligible";

/**
 * Status and reason are one value so a view can never be recorded with the
 * reason from a different decision than its status.
 */
export type AutomaticApplyHandoffDecision =
  | { status: "pending" | "eligible"; skip?: undefined }
  | { status: "ineligible"; skip?: IAutomaticApplySkip };

const PENDING: AutomaticApplyHandoffDecision = { status: "pending" };

// One shared object rather than a fresh literal per call: the caller keys its
// view effect on the skip's identity, so an equal-but-new skip on mount would
// record the view a second time.
const BLANK_UTM_SOURCE_SKIP: IAutomaticApplySkip = {
  reason: AutomaticApplySkipReason.BLANK_UTM_SOURCE,
};

function ineligible(skip: IAutomaticApplySkip): AutomaticApplyHandoffDecision {
  return { status: "ineligible", skip };
}

function describeResponse(response: Response): string {
  return [
    `${response.status} ${response.statusText}`.trim(),
    `cf-mitigated=${response.headers.get("cf-mitigated") ?? ""}`,
    `content-type=${response.headers.get("content-type") ?? ""}`,
  ].join("; ");
}

/**
 * The config route answers 503 with an `upstreamError` body when it could not
 * reach the API, so it can be told apart from a failure in front of the route
 * (Next itself, or Cloudflare), which carries no such body.
 */
async function classifyErrorResponse(
  response: Response
): Promise<IAutomaticApplySkip> {
  try {
    const body = await response.json();
    if (typeof body?.upstreamError === "string") {
      return {
        reason: AutomaticApplySkipReason.CONFIG_UPSTREAM_ERROR,
        detail: body.upstreamError,
      };
    }
  } catch {
    // Not our JSON - fall through to the plain HTTP error.
  }
  return {
    reason: AutomaticApplySkipReason.CONFIG_HTTP_ERROR,
    detail: describeResponse(response),
  };
}

function classifyFetchRejection(
  error: unknown,
  hasTimedOut: boolean
): IAutomaticApplySkip {
  if (hasTimedOut) {
    return {
      reason: AutomaticApplySkipReason.CONFIG_FETCH_TIMEOUT,
      detail: `after ${CONFIG_FETCH_TIMEOUT_MS}ms`,
    };
  }
  // The browser gives a bare TypeError for an extension or CSP block, DNS
  // failure, reset connection and being offline alike, with a message that
  // varies by browser rather than by cause. onLine is the only extra signal.
  const isOnline = typeof navigator === "undefined" ? "" : navigator.onLine;
  return {
    reason: AutomaticApplySkipReason.CONFIG_FETCH_FAILED,
    detail: `${describeError(error)}; online=${isOnline}`,
  };
}

function getImmediateSkip(
  suppression: IAutomaticApplySkip | undefined,
  utmSource: string | null
): IAutomaticApplySkip | undefined {
  if (suppression) {
    return suppression;
  }
  return utmSource?.trim() ? undefined : BLANK_UTM_SOURCE_SKIP;
}

export function useAutomaticApplyHandoffEligibility(
  jobDetails: JobResponseEntity,
  suppression?: IAutomaticApplySkip
): AutomaticApplyHandoffDecision {
  const searchParams = useSearchParams();
  const organizationId = jobDetails.organization.id;
  const utmSource = searchParams.get("utm_source");
  // A hand-off always requires a utm_source, so traffic without one is decided
  // here without a network call. That keeps organic, direct and internal
  // visitors - the overwhelming majority - out of the pending state entirely,
  // so they never see the interstitial even for a moment.
  const [decision, setDecision] = useState<AutomaticApplyHandoffDecision>(
    () => {
      const immediateSkip = getImmediateSkip(suppression, utmSource);
      return immediateSkip ? ineligible(immediateSkip) : PENDING;
    }
  );

  useEffect(() => {
    const immediateSkip = getImmediateSkip(suppression, utmSource);
    if (immediateSkip) {
      // Keep the current decision when it already carries this skip, so the
      // mount pass does not hand the caller a new object for the same answer.
      setDecision((current) =>
        current.skip === immediateSkip ? current : ineligible(immediateSkip)
      );
      return undefined;
    }
    let isCancelled = false;
    let hasTimedOut = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      hasTimedOut = true;
      controller.abort();
    }, CONFIG_FETCH_TIMEOUT_MS);

    async function decide(): Promise<AutomaticApplyHandoffDecision> {
      let response: Response;
      try {
        response = await fetch(CONFIG_ENDPOINT, {
          signal: controller.signal,
        });
      } catch (error) {
        return ineligible(classifyFetchRejection(error, hasTimedOut));
      }
      if (!response.ok) {
        return ineligible(await classifyErrorResponse(response));
      }
      let config: unknown;
      try {
        config = await response.json();
      } catch (error) {
        // A timeout can also land while the body is still streaming.
        if (hasTimedOut) {
          return ineligible(classifyFetchRejection(error, hasTimedOut));
        }
        return ineligible({
          reason: AutomaticApplySkipReason.CONFIG_INVALID_JSON,
          detail: `${describeResponse(response)}; ${describeError(error)}`,
        });
      }
      const { clients, excludedUserAgents } = (config ?? {}) as {
        clients?: unknown;
        excludedUserAgents?: unknown;
      };
      const malformedFields = [
        Array.isArray(clients) ? undefined : "clients",
        Array.isArray(excludedUserAgents) ? undefined : "excludedUserAgents",
      ].filter(Boolean);
      if (malformedFields.length > 0) {
        return ineligible({
          reason: AutomaticApplySkipReason.CONFIG_MALFORMED,
          detail: `not an array: ${malformedFields.join(", ")}`,
        });
      }
      const evaluation = evaluateAutomaticApply({
        clients: clients as IAutomaticApplyHandoffClient[],
        excludedUserAgents: excludedUserAgents as string[],
        organizationId,
        utmSource,
        userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
        applyUrl: buildJobApplyUrl(jobDetails),
        isApplyViaEmail: jobDetails.organization.is_apply_via_email,
        applyEmail: jobDetails.organization.apply_email,
        sourceInternalId: jobDetails.source_internal_id,
      });
      if (evaluation.eligible) {
        return { status: "eligible" };
      }
      return ineligible({
        reason: evaluation.reason,
        detail: evaluation.detail,
      });
    }

    // Anything decide() does not classify still fails closed to the normal
    // page, and records what was thrown rather than nothing.
    decide()
      .catch((error: unknown) =>
        ineligible({
          reason: AutomaticApplySkipReason.EVALUATION_ERROR,
          detail: describeError(error),
        })
      )
      .then((result) => {
        if (!isCancelled) {
          setDecision(result);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [organizationId, utmSource, suppression, jobDetails]);

  return decision;
}
